import * as _ from 'lodash'
import React from 'react';
import jobFactory from '../models/jobModel';
import jobCostNoteFactory from '../models/jobCostNoteModel';
import jobCostScheduleItemFactory from '../models/jobCostScheduleItemModel';
import componentServices from '../componentServices';
import * as contractorActions from '../contractorActions';
import enums from '../enum';
import util from '../util';
import valueFilters from '../valueFilters';
import { DayPickerInput } from './dayPickerInput';
import { getModel, ModelCollection } from 'Scripts/store';

export default class JobCostEditSubmit extends React.Component<any, any> {
	private _afterNoteSaved;

	constructor(props) {
		super(props);

		this.state = JobCostEditSubmit.getDerivedStateFromProps(props, {});
		this._afterNoteSaved = null;

		this.handleButtonClick = this.handleButtonClick.bind(this);
		this.onDateChange = this.onDateChange.bind(this);
		this.handlePOChange = this.handlePOChange.bind(this);
	}

	static getDerivedStateFromProps(props, currentState) {
		const { store } = componentServices;
		const { jobCost } = props;
		const state = _.assign({ builderPo: '' }, currentState);
		const job = getModel({ store, collection: ModelCollection.jobs, key: jobCost.jobKey });
		const builder = job.builderKey ? getModel({ store, collection: ModelCollection.contractors, key: job.builderKey }) : undefined;

		if ((!job.builderKey || builder) && (state.jobCostKey !== jobCost.key || state.jobCostItemVersion !== jobCost.itemVersion)) {
			if (state.jobCostKey !== jobCost.key) {
				const proposedDeliveryItem = _.find(jobCost.scheduleItems, { type: jobCostScheduleItemFactory.TYPES.PROPOSED_DELIVERY });
				const autoGenPos = _.filter(job.getSystemGeneratedPayments(jobCost.key), { transactionMethod: 'purchase-order' });

				state.proposedDeliveryDate = (proposedDeliveryItem && proposedDeliveryItem.date) || '';

				if (autoGenPos.length) {
					state.builderPo = autoGenPos[0].transactionReference;
				}
			}

			state.job = job;
			state.builder = builder;
			state.jobCostKey = jobCost.key;
			state.jobCostItemVersion = jobCost.itemVersion;
		}

		if (!builder && job.builderKey) {
			contractorActions.fetchContractor({ key: job.builderKey });
		}

		return state;
	}

	onDateChange({ date }: { date?: Date}) {
		this.setState({ proposedDeliveryDate: date });
	}

	handleNoteChange(type, e) {
		const options = e.type === 'blur' ? { autoSave: true } : { autoSave: false };
		const newVal = e.type === 'blur' ? valueFilters.clean(e.target.value) : e.target.value;

		(options as any).callback = (err) => {
			if (!err && this._afterNoteSaved) {
				this._afterNoteSaved();
				this._afterNoteSaved = null;
			}
		};

		this.props.updateModel(jobCost => {
			const updateTarget = jobCost.proxiedContract || jobCost;
			const note = _.find(updateTarget.notes, { type }) || jobCostNoteFactory({ type });

			note.content = newVal;
			updateTarget.notes.set(note.key, note);
		}, options);
	}

	handlePOChange(e) {
		const newVal = e.type === 'blur' ? valueFilters.clean(e.target.value) : e.target.value;
		// set state to force a re-render
		this.setState(previousState => {
			const newState = _.assign({}, previousState);

			newState.builderPo = newVal;

			return newState;
		});
	}

	hasDeliveryDate() {
		return !!this.state.proposedDeliveryDate;
	}

	canSubmit() {
		const { builder, builderPo, job } = this.state;
		const builderPasses = !builder || builder.currentStatus !== 'locked';
		const poPasses = !job || job.businessSegment !== enums.businessSegments.Builder || !!builderPo;

		return builderPasses && poPasses && this.props.jobCost.currentStatus === 'locked' && this.hasDeliveryDate();
	}

	handleButtonClick() {
		const { summary } = this.props;
		const { job, builderPo, proposedDeliveryDate } = this.state;
		const saveState = componentServices.store.getIn(['viewState', 'globalSave', 'state']);
		const options = { autoSave: true };

		if (!this.canSubmit()) { return; }

		if (saveState !== 0) {
			this._afterNoteSaved = () => {
				this.handleButtonClick();
			};
			return;
		}

		this.props.updateModel(jobCost => {
			const updateTarget = jobCost.proxiedContract || jobCost;
			const scheduleItem = _.find(updateTarget.scheduleItems, { type: jobCostScheduleItemFactory.TYPES.PROPOSED_DELIVERY }) || jobCostScheduleItemFactory({ type: jobCostScheduleItemFactory.TYPES.PROPOSED_DELIVERY });

			updateTarget.statusLog.add({ status: 'pending-approval' });

			scheduleItem.date = proposedDeliveryDate;
			updateTarget.scheduleItems.set(scheduleItem.key, scheduleItem);

			if (job.businessSegment === enums.businessSegments.Builder) {
				const jobUpdate = jobFactory(job.toDocument());
				const autoGenPos = _.filter(jobUpdate.getSystemGeneratedPayments(updateTarget.key), { transactionMethod: 'purchase-order' });
				if (autoGenPos.length) {
					const payment = autoGenPos[0];
					payment.transactionReference = builderPo;
					payment.amount = summary.grandTotalContract;
				}
				else if (summary.grandTotalContract) {
					jobUpdate.payments.add({ jobCostKey: updateTarget.key, type: 'other', transactionMethod: 'purchase-order', transactionReference: builderPo, amount: summary.grandTotalContract, systemGenerated: true });
				}

				const jobPatch = jobUpdate.getPatch(job);

				if (jobPatch.length) {
					(options as any).extraneousPatch = [{
						op: 'replace',
						path: '../job',
						value: jobPatch
					}];
				}
			}

			// updateTarget.submittedSummaryData = summary.toJSON();
		}, options);
	}

	render() {
		const { jobCost } = this.props;
		const { builderPo, job, proposedDeliveryDate } = this.state;
		const updateTarget = jobCost.proxiedContract || jobCost;
		const operationsNote = updateTarget.getNoteContent(jobCostNoteFactory.NOTE_TYPES.CONTRACT_FOR_OPERATIONS);
		const installerNote = updateTarget.getNoteContent(jobCostNoteFactory.NOTE_TYPES.CONTRACT_FOR_INSTALLER);

		const submitButtonClasses = ['action', 'iconLabel', 'submitToOrderDesk'];

		if (['locked', 'rejected'].indexOf(jobCost.currentStatus) === -1) {
			submitButtonClasses.push('locked');
		}
		if (!this.canSubmit()) {
			submitButtonClasses.push('disabled');
		}

		const submitButtonText = ['locked', 'rejected'].indexOf(jobCost.currentStatus) !== -1 ? 'SUBMIT TO ORDER DESK' : 'IN REVIEW BY ORDER DESK';
		const submitButton = ['locked', 'rejected', 'pending-approval'].indexOf(jobCost.currentStatus) !== -1 ? (<button className={submitButtonClasses.join(' ')} onClick={this.handleButtonClick}><span>{submitButtonText}</span></button>) : null;

		return (
			<section className="jobCostFinalize">
				{ /* Final Details */}
				<section>
					<header>
						<span>SUBMIT</span>
					</header>
					<div className={`inner compact ${(['locked', 'rejected'].indexOf(jobCost.currentStatus) !== -1 ? '' : 'readOnly')}`}>

						<table cellPadding="0" cellSpacing="0">
							<tbody>
								{job && job.businessSegment === enums.businessSegments.Builder
									? <tr className={!builderPo ? 'required' : ''}>
										<td />
										<td><strong>Builder PO No.</strong></td>
										<td><input type="text" className="" placeholder="Enter the Builder PO Number" autoCapitalize="" autoCorrect="off" value={util.reactVal(builderPo)} onChange={this.handlePOChange} onBlur={this.handlePOChange} /></td>
										<td />
									</tr> : null
								}
								<tr className={this.hasDeliveryDate() ? '' : 'required'}>
									<td />
									<td><strong>Proposed Delivery Date</strong></td>
									<td>
										<DayPickerInput placeholder="Select a Date" selectedDate={ proposedDeliveryDate } isVertical={ true } onDateSelected={ this.onDateChange } />
									</td>
									<td />
								</tr>
								<tr>
									<td />
									<td><strong>Notes for Operations</strong></td>
									<td><textarea style={{ height: '80px', minHeight: 'auto', position: 'static' }} value={operationsNote} onChange={this.handleNoteChange.bind(this, jobCostNoteFactory.NOTE_TYPES.CONTRACT_FOR_OPERATIONS)} onBlur={this.handleNoteChange.bind(this, jobCostNoteFactory.NOTE_TYPES.CONTRACT_FOR_OPERATIONS)} /></td>
									<td />
								</tr>
								<tr>
									<td />
									<td><strong>Notes for Installer</strong></td>
									<td><textarea style={{ height: '80px', minHeight: 'auto', position: 'static' }} value={installerNote} onChange={this.handleNoteChange.bind(this, jobCostNoteFactory.NOTE_TYPES.CONTRACT_FOR_INSTALLER)} onBlur={this.handleNoteChange.bind(this, jobCostNoteFactory.NOTE_TYPES.CONTRACT_FOR_INSTALLER)} /></td>
									<td />
								</tr>
								<tr>
									<td />
									<td />
									<td>{submitButton}</td>
									<td />
								</tr>
							</tbody>
						</table>
					</div>
				</section>

			</section>
		);
	}
}
